import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { familyTranslations } from '../data/translations';

const FAQSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [openIndex, setOpenIndex] = useState(null);
  const sectionRef = useRef(null);
  const { lang } = useLanguage();
  const t = familyTranslations[lang].faqData;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section ref={sectionRef} id="faq" className="py-24 md:py-32 bg-white">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div
          className={`text-center mb-16 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#5B4FE9]">
            {t.label}
          </span>
          <h2
            className="mt-4 text-3xl sm:text-4xl md:text-[44px] font-bold text-gray-900 leading-[1.15] tracking-tight"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            {t.title}
          </h2>
          <p className="mt-5 text-lg text-gray-500 leading-relaxed">
            {t.subtitle}
          </p>
        </div>

        {/* FAQ Items */}
        <div className="space-y-3">
          {t.questions.map((item, i) => (
            <div
              key={i}
              className={`border border-gray-100 rounded-xl overflow-hidden transition-all duration-500 ${
                isVisible
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-4'
              } ${
                openIndex === i ? 'shadow-md border-gray-200' : 'hover:border-gray-200'
              }`}
              style={{ transitionDelay: `${200 + i * 80}ms` }}
            >
              <button
                onClick={() => toggleFAQ(i)}
                className="w-full flex items-center justify-between px-6 py-5 text-left"
              >
                <span className="text-[15px] font-semibold text-gray-900 pr-4">
                  {item.question}
                </span>
                <ChevronDown
                  className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform duration-300 ${
                    openIndex === i ? 'rotate-180' : ''
                  }`}
                />
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === i ? 'max-h-48 opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="px-6 pb-5">
                  <p className="text-sm text-gray-500 leading-relaxed">{item.answer}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
